import {
  createExtension,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import { UnifiedThemeProvider } from '@backstage/theme';
import { Moon, Sun } from 'lucide-react';
import { useEffect } from 'react';
import { portalDarkTheme, portalTheme } from '../../theme';

/** Adds/removes the Tailwind `.dark` class so shadcn-styled content follows
 * whichever MUI theme Backstage has resolved as active (explicit user choice,
 * falling back to the OS `prefers-color-scheme` setting). */
function useSyncDarkClass(isDark: boolean) {
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    return () => root.classList.remove('dark');
  }, [isDark]);
}

const disableDefaultLightTheme = createExtension({
  kind: 'theme',
  name: 'light',
  attachTo: { id: 'api:app/app-theme', input: 'themes' },
  disabled: true,
  output: [],
  factory: () => [],
});

const disableDefaultDarkTheme = createExtension({
  kind: 'theme',
  name: 'dark',
  attachTo: { id: 'api:app/app-theme', input: 'themes' },
  disabled: true,
  output: [],
  factory: () => [],
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    disableDefaultLightTheme,
    disableDefaultDarkTheme,
    ThemeBlueprint.make({
      name: 'vivicta-light',
      params: {
        theme: {
          id: 'vivicta-light',
          title: 'Vivicta Light',
          variant: 'light',
          icon: <Sun />,
          Provider: ({ children }) => {
            useSyncDarkClass(false);
            return (
              <UnifiedThemeProvider theme={portalTheme}>{children}</UnifiedThemeProvider>
            );
          },
        },
      },
    }),
    ThemeBlueprint.make({
      name: 'vivicta-dark',
      params: {
        theme: {
          id: 'vivicta-dark',
          title: 'Vivicta Dark',
          variant: 'dark',
          icon: <Moon />,
          Provider: ({ children }) => {
            useSyncDarkClass(true);
            return (
              <UnifiedThemeProvider theme={portalDarkTheme}>{children}</UnifiedThemeProvider>
            );
          },
        },
      },
    }),
  ],
});